import { useState } from "react";
import {
  Box, Stack, Typography, Paper, Button, Chip, Snackbar, Alert, Divider,
} from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import VerifiedIcon from "@mui/icons-material/Verified";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { useQuery, useMutation } from "convex/react";
import { api } from "../../../../convex/_generated/api";
import type { Id } from "../../../../convex/_generated/dataModel";

/**
 * Admin view of client posting-rules questionnaire submissions.
 * Each signed submission stores the full answers JSON (schema
 * altelix.pipeline.rules/v1) — download it and feed it to altelix-rules-apply.py.
 */
export function PostingRulesAdminPage() {
  const rows = useQuery(api.postingRules.list, {});
  const deleteSubmission = useMutation(api.postingRules.deleteSubmission);
  const [snack, setSnack] = useState<string | null>(null);

  const remove = async (id: Id<"postingRulesSubmissions">, who?: string) => {
    if (!window.confirm(`Delete the submission from ${who || "this respondent"}? This cannot be undone.`)) return;
    await deleteSubmission({ id });
    setSnack("Submission deleted");
  };

  const fmt = (ts: number) => new Date(ts).toLocaleString();

  const download = (raw: string, who?: string) => {
    const blob = new Blob([raw], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    const slug = (who || "altelix").toLowerCase().replace(/[^a-z0-9]+/g, "-");
    a.href = url;
    a.download = `altelix-posting-rules-${slug}.json`;
    a.click();
    URL.revokeObjectURL(url);
    setSnack("Downloaded — run it through altelix-rules-apply.py");
  };

  const copy = async (raw: string) => {
    try { await navigator.clipboard.writeText(raw); setSnack("JSON copied to clipboard"); }
    catch { setSnack("Copy failed"); }
  };

  return (
    <Box sx={{ p: 3, maxWidth: 1000, mx: "auto" }}>
      <Typography variant="h5" sx={{ fontWeight: 800, mb: 0.5 }}>
        Posting Rules — Submissions
      </Typography>
      <Typography variant="body2" sx={{ color: "var(--c-text-secondary)", mb: 3 }}>
        Signed business-rules questionnaires submitted by the client. Download a submission
        and run it through <code>altelix-rules-apply.py</code> to generate the implementation plan.
      </Typography>

      {rows === undefined && (
        <Typography variant="body2" sx={{ color: "var(--c-text-muted)" }}>Loading…</Typography>
      )}
      {rows && rows.length === 0 && (
        <Paper sx={{ p: 4, textAlign: "center", color: "var(--c-text-muted)" }}>
          No submissions yet. The client can fill the questionnaire from the
          <strong> Posting Rules </strong> menu item.
        </Paper>
      )}

      <Stack spacing={2}>
        {(rows ?? []).map((r) => (
          <Paper key={r._id} sx={{ p: 2.5, borderRadius: 3 }}>
            <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 1 }}>
              <VerifiedIcon sx={{ color: r.approvalConfirmed ? "#1f9d57" : "var(--c-text-muted)" }} />
              <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                {r.approvalSignedBy || r.respondentName || "Unknown"}
              </Typography>
              {r.approvalTitle && (
                <Typography variant="caption" sx={{ color: "var(--c-text-muted)" }}>
                  {r.approvalTitle}
                </Typography>
              )}
              <Box sx={{ flex: 1 }} />
              <Chip
                label={r.approvalConfirmed ? "Signed" : "Unsigned"}
                size="small"
                color={r.approvalConfirmed ? "success" : "default"}
              />
            </Stack>
            <Stack direction="row" spacing={3} sx={{ mb: 1.5, flexWrap: "wrap" }}>
              <Field label="Submitted" value={fmt(r.submittedAt)} />
              <Field label="Sign-off date" value={r.approvalDate || "—"} />
              <Field label="Answers" value={String(r.answerCount)} />
              <Field label="By" value={r.submittedByEmail || r.respondentRole || "—"} />
              <Field label="Schema" value={r.schemaVersion} />
            </Stack>
            <Divider sx={{ mb: 1.5 }} />
            <Stack direction="row" spacing={1}>
              <Button size="small" variant="contained" startIcon={<DownloadIcon />}
                onClick={() => download(r.raw, r.approvalSignedBy || r.respondentName)}>
                Download JSON
              </Button>
              <Button size="small" variant="outlined" startIcon={<ContentCopyIcon />}
                onClick={() => copy(r.raw)}>
                Copy JSON
              </Button>
              <Box sx={{ flex: 1 }} />
              <Button size="small" color="error" startIcon={<DeleteOutlineIcon />}
                onClick={() => remove(r._id, r.approvalSignedBy || r.respondentName)}>
                Delete
              </Button>
            </Stack>
          </Paper>
        ))}
      </Stack>

      <Snackbar
        open={!!snack}
        autoHideDuration={3500}
        onClose={() => setSnack(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        {snack ? <Alert severity="info" variant="filled" onClose={() => setSnack(null)}>{snack}</Alert> : undefined}
      </Snackbar>
    </Box>
  );
}

function Field({ label, value }: { label: string; value: string }) {
  return (
    <Box>
      <Typography variant="caption" sx={{ color: "var(--c-text-muted)", textTransform: "uppercase", letterSpacing: "0.5px", fontSize: "0.6rem", display: "block" }}>
        {label}
      </Typography>
      <Typography variant="body2" sx={{ fontWeight: 500 }}>{value}</Typography>
    </Box>
  );
}
